import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { API_BASE_URL } from '@synergy/config';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { BonusSettlementSupplierDetailEditFormValues } from '../models/bonus-settlement-supplier-detail-edit-form-values';
import { BonusSettlementSupplierDetailFormValues } from '../models/bonus-settlement-supplier-detail-form-values';
import { BonusSettlementsSupplierDetailApi } from '../api/bonus-settlements-supplier-detail-api';

const api = new BonusSettlementsSupplierDetailApi(API_BASE_URL);

interface GetBonusSettlementsSupplierDetailParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
}

interface UpdateSinglePoolBonusParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    data: BonusSettlementSupplierDetailEditFormValues;
}

interface UpdateAllPoolBonusParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    noteToCustomer: string;
}

interface SaveBonusSettlementSupplierDetailParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    data: BonusSettlementSupplierDetailFormValues;
}

interface SubmitBonusSettlementSupplierDetailParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    data: BonusSettlementSupplierDetailFormValues;
}

interface UpdateNoteFromSupplierParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    noteFromSupplier: string;
}

interface UpdateInternalNoteParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    internalNote: string;
}

interface UpdateBonusSettlementSupplierFilesParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    files: File[];
    deletedFileIds: number[];
}

interface UpdateReclaimBonusSettlementCustomerParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    customerBonusSettlementId: number;
}

interface UpdateCustomerParams {
    bonusSettlementId: number;
    supplierBonusSettlementId: number;
    customerId: number;
}

export const resetBonusSettlementsSupplierDetail = createAction<void>(
    'bonus-settlements/resetBonusSettlementsSupplierDetail'
);
export const getBonusSettlementsSupplierDetail = createAsyncThunk(
    'bonus-settlements/getBonusSettlementsSupplierDetail',
    async (params: GetBonusSettlementsSupplierDetailParams, thunkAPI) => {
        const response = await api.getBonusSettlementSupplierDetail(
            params.bonusSettlementId,
            params.supplierBonusSettlementId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        return response.result;
    }
);

export const updateSinglePoolBonus = createAsyncThunk(
    'bonus-settlements/updateSinglePoolBonus',
    async (params: UpdateSinglePoolBonusParams, thunkAPI) => {
        const response = await api.updateSingleNoteToCustomer(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.data.id,
            {
                noteToCustomer: params.data.noteToCustomer,
                actualBonusValues: params.data.actualBonusValues,
            }
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.updateSinglePoolBonusSuccess'))
        );

        return params.data;
    }
);

export const updateAllPoolBonus = createAsyncThunk(
    'bonus-settlements/updateAllPoolBonus',
    async (params: UpdateAllPoolBonusParams, thunkAPI) => {
        const response = await api.updateAllNoteToCustomer(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.noteToCustomer
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.updateAllPoolBonusSuccess'))
        );

        return params.noteToCustomer;
    }
);

export const saveBonusSettlementSupplierDetail = createAsyncThunk(
    'bonus-settlements/saveBonusSettlementSupplierDetail',
    async (params: SaveBonusSettlementSupplierDetailParams, thunkAPI) => {
        const response = await api.saveBonusSettlementSupplierDetail(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            {
                customerBonusSettlements: params.data.customerBonusSettlements.map(settlement => ({
                    id: settlement.id,
                    bonusConditionValues: settlement.bonusConditionValues,
                    isFinished: settlement.isFinished,
                    noteToCustomer: settlement.noteToCustomer,
                    isReclaimed: settlement.isReclaimed,
                })),
            }
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return null;
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.saveBonusSettlementSupplierDetailSuccess'))
        );

        return null;
    }
);

export const submitBonusSettlementSupplierDetail = createAsyncThunk(
    'bonus-settlements/submitBonusSettlementSupplierDetail',
    async (params: SubmitBonusSettlementSupplierDetailParams, thunkAPI) => {
        let response;

        response = await api.saveBonusSettlementSupplierDetail(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.data
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        response = await api.submitBonusSettlementSupplierDetail(
            params.bonusSettlementId,
            params.supplierBonusSettlementId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(
                Snackbar.PoolNotification.success('app.snackbar.submitBonusSettlementSupplierDetailSuccess')
            )
        );

        return null;
    }
);

export const updateNoteFromSupplier = createAsyncThunk(
    'bonus-settlements/updateNoteFromSupplier',
    async (params: UpdateNoteFromSupplierParams, thunkAPI) => {
        const response = await api.updateNoteFromSupplier(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.noteFromSupplier
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(
                Snackbar.PoolNotification.success(
                    'app.snackbar.updateBonusSettlementSupplierDetailSupplierCommentSuccess'
                )
            )
        );

        return params.noteFromSupplier;
    }
);

export const updateInternalNote = createAsyncThunk(
    'bonus-settlements/updateInternalNote',
    async (params: UpdateInternalNoteParams, thunkAPI) => {
        const response = await api.updateInternalNote(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.internalNote
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(
                Snackbar.PoolNotification.success(
                    'app.snackbar.updateBonusSettlementSupplierDetailInternalCommentSuccess'
                )
            )
        );

        return params.internalNote;
    }
);

export const updateSupplierBonusSettlementFiles = createAsyncThunk(
    'bonus-settlements/uploadFiles',
    async (params: UpdateBonusSettlementSupplierFilesParams, thunkAPI) => {
        const responses = await Promise.all(
            params.files.map(file => api.uploadFile(params.bonusSettlementId, params.supplierBonusSettlementId, file))
        );
        let deleteResponse;

        if (params.deletedFileIds.length > 0) {
            deleteResponse = await api.deleteFiles(
                params.bonusSettlementId,
                params.supplierBonusSettlementId,
                params.deletedFileIds
            );
        }

        const allResponsesSuccessful = responses.every(response => response.success);
        const isDeleteResponseSuccessful = deleteResponse ? deleteResponse.success : true;

        if (!allResponsesSuccessful || !isDeleteResponseSuccessful) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));

            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.filesUpdated')));

        // What the reducer needs to bring the list up to date; without it the
        // list stayed as loaded until the page was reloaded (R-196).
        return {
            uploaded: responses.flatMap(response => (response.success ? [response.result] : [])),
            deletedFileIds: params.deletedFileIds,
        };
    }
);
export const addCustomer = createAsyncThunk(
    'bonus-settlements/addCustomer',
    async (params: UpdateCustomerParams, thunkAPI) => {
        const response = await api.addCustomer(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.customerId
        );

        if (!response.success) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(
            Snackbar.enqueue(
                Snackbar.PoolNotification.success(
                    'app.snackbar.updateBonusSettlementSupplierDetailSupplierAddCustomerSuccess'
                )
            )
        );

        return response.result;
    }
);

export const removeCustomer = createAsyncThunk(
    'bonus-settlements/removeCustomer',
    async (params: UpdateCustomerParams, thunkAPI) => {
        const response = await api.deleteCustomer(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.customerId
        );

        if (!response.success) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.customerDeleted')));

        return params.customerId;
    }
);

export const reclaim = createAsyncThunk(
    'bonus-settlements/reclaim',
    async (params: UpdateReclaimBonusSettlementCustomerParams, thunkAPI) => {
        const response = await api.reclaimCustomer(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.customerBonusSettlementId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        return params;
    }
);

export const revertReclaim = createAsyncThunk(
    'bonus-settlements/revert-reclaim',
    async (params: UpdateReclaimBonusSettlementCustomerParams, thunkAPI) => {
        const response = await api.revertReclaimCustomer(
            params.bonusSettlementId,
            params.supplierBonusSettlementId,
            params.customerBonusSettlementId
        );

        if (response.success === false) {
            thunkAPI.dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.error')));
            return thunkAPI.rejectWithValue(null);
        }

        return params;
    }
);
